import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelineStageExecutions, queryPipelineStages } from '@/features/pipelines/data';
import { buildStageHistoryRows } from '@/features/pipelines/hooks/stageHistoryRows';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import * as stagePageQueryGates from '@/features/pipelines/hooks/stagePageQueryGates';
import { useStageFilterOptions } from '@/features/pipelines/hooks/useStageFilterOptions';
import {
  resolveExpandedStageHistoryJobStages,
  useStageHistoryJobs,
} from '@/features/pipelines/hooks/useStageHistoryJobs';
import { useStageMetricCards } from '@/features/pipelines/hooks/useStageMetricCards';
import { useStagesPageFilterOptionCounts } from '@/features/pipelines/hooks/useStagesPageFilterOptionCounts';
import {
  buildStageCounts,
  buildFallbackStageExecutions,
  buildStageExecutionMetricSummary,
  buildStageMetricBucketsByDate,
  buildStageStatusCountsByDate,
  buildStageStatusSeries,
  resolveVisibleStageStatuses,
  resolveStageExecutionTimestamp,
  sortHistoryRows,
} from '@/features/pipelines/hooks/useStagesPageViewModel.helpers';
import { buildPreviousCustomWindow } from '@/features/pipelines/utils/buildMetricCharts';
import { useProjectMetricThresholds } from '@/features/projects';
import {
  formatRangeWindowNoDataText,
  useLazyComparisonDateRange,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { buildBuildMetricOptions } from '@/features/quality/sections/qualityMetricOptions';
import {
  buildMetricsChartModelFromBuckets,
  buildMetricsChartToolbarState,
} from '@/features/shared/components/chartPanels/chartPanelAdapters';
import {
  useDailyHistoryPanelPageReset,
  useDailyHistoryPanelState,
} from '@/features/shared/hooks/useDailyHistoryPanelState';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import {
  buildChartDatesForDayRange,
  resolveWindowDayKeys,
} from '@/features/shared/utils/chartDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import {
  queryKeys,
  useIsAuthenticated,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { StageSortKey } from '@/features/pipelines/components/StagesTablePanel';
import type { UseStagesPageViewModelResult } from '@/features/pipelines/hooks/useStagesPageViewModel.types';
import type { BuildStatus, PipelineStageExecutionListItemDto } from '@flowqa/shared';

export const useStagesPageViewModel = (): UseStagesPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const {
    activateComparisonForRange,
    handleEndDayChange,
    handleStartDayChange,
    range,
    selectedEndDay,
    selectedStartDay,
  } = useLazyComparisonDateRange('7d');
  const [stageSearchValue, setStageSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<BuildStatus[]>([]);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<string[]>([]);
  const [selectedStageNames, setSelectedStageNames] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:stages');
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const {
    page: historyPage,
    pageSize: historyPageSize,
    sortKey: historySortKey,
    sortDirection: historySortDirection,
    isTransitionLoading: isHistoryTransitionLoading,
    setPage: setHistoryPage,
    setPageSize: setHistoryPageSize,
    setSortKey: setHistorySortKey,
    setSortDirection: setHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'prefixLabel',
  });
  const [stageSortKey, setStageSortKey] = useState<StageSortKey>('startedAt');
  const [stageSortDirection, setStageSortDirection] = useState<'asc' | 'desc'>('desc');
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const { tagOptions } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { pipelineOptions, stageOptions } = useStageFilterOptions({
    isAuthenticated,
    effectiveProjectId,
    selectedOrganisationId,
    selectedPipelineIds,
    setSelectedStageNames,
  });
  const { thresholds } = useProjectMetricThresholds(effectiveProjectId);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const previousComparisonWindow = useMemo(() => {
    if (isLatestMode || isAllMode) {
      return { startIso: null, endIso: null };
    }
    if (hasCustomDates) {
      return buildPreviousCustomWindow(selectedStartDay, selectedEndDay);
    }
    return { startIso: previousRangeStartIso, endIso: previousRangeEndIso };
  }, [
    hasCustomDates,
    isAllMode,
    isLatestMode,
    previousRangeEndIso,
    previousRangeStartIso,
    selectedEndDay,
    selectedStartDay,
  ]);
  const showDelta =
    !isLatestMode &&
    !isAllMode &&
    previousComparisonWindow.startIso !== null &&
    previousComparisonWindow.endIso !== null;
  const forcedLoadState = searchParams.get('loadState');
  const showForcedLoadingState = forcedLoadState === 'loading';

  const baseStageFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildStageNameFilters(selectedStageNames),
        buildInFilter('tags', selectedTags),
      ),
    [selectedPipelineIds, selectedStageNames, selectedStatuses, selectedTags],
  );
  const latestBoundsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/latest-bounds',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
    ),
    enabled: isAuthenticated && isLatestMode,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
              ),
            },
          ),
      ),
  });
  const latestTimestamp = useMemo(() => {
    return (
      (latestBoundsQuery.data?.items ?? [])
        .map((item) => resolveStageExecutionTimestamp(item))
        .filter((value): value is Date => value instanceof Date)
        .sort((left, right) => right.getTime() - left.getTime())[0] ?? null
    );
  }, [latestBoundsQuery.data?.items]);
  const hasLatestTimestamp = latestTimestamp instanceof Date;
  const effectiveStartDay =
    selectedStartDay ??
    (isLatestMode
      ? hasLatestTimestamp
        ? dayjs(latestTimestamp).format('YYYY-MM-DD')
        : null
      : (rangeStartIso?.slice(0, 10) ?? null));
  const effectiveEndDay =
    selectedEndDay ??
    (isLatestMode
      ? hasLatestTimestamp
        ? dayjs(latestTimestamp).format('YYYY-MM-DD')
        : null
      : (rangeEndIso?.slice(0, 10) ?? null));
  const previousStartDay = previousComparisonWindow.startIso?.slice(0, 10) ?? null;
  const previousEndDay = previousComparisonWindow.endIso?.slice(0, 10) ?? null;
  const currentChartDates = useMemo(
    () => buildChartDatesForDayRange(effectiveStartDay, effectiveEndDay),
    [effectiveEndDay, effectiveStartDay],
  );
  const previousChartDates = useMemo(
    () => buildChartDatesForDayRange(previousStartDay, previousEndDay),
    [previousEndDay, previousStartDay],
  );

  const currentStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'rows',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: mergeFilters(
              buildContainsFilter('stageName', stageSearchValue),
              baseStageFilters,
              buildDateRangeFilters(
                'startedAt',
                effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
              ),
            ),
          }),
      ),
  });
  const currentStageExecutionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'executions',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
                buildDateRangeFilters(
                  'startedAt',
                  effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                  effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
                ),
              ),
            },
          ),
      ),
  });
  const currentStages = currentStagesQuery.data?.items ?? [];
  const currentStageExecutions = currentStageExecutionsQuery.data?.items ?? [];
  const expandedStages = useMemo(
    () => resolveExpandedStageHistoryJobStages(currentStages, expandedRowIds),
    [currentStages, expandedRowIds],
  );
  const {
    jobsByStageId,
    loadingStageIds,
    isError: hasStageJobsError,
  } = useStageHistoryJobs({
    isAuthenticated,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedProjectId: effectiveProjectId,
    selectedStatuses,
    selectedTags,
    expandedStages,
    effectiveStartDay,
    effectiveEndDay,
    isLatestMode,
    hasLatestTimestamp,
  });
  const previousStageExecutionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'executions',
      'previous',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      previousStartDay,
      previousEndDay,
    ),
    enabled: stagePageQueryGates.shouldLoadStagePreviousWindowQuery({
      isAuthenticated,
      showDelta,
      hasPreviousWindowBounds: previousStartDay !== null && previousEndDay !== null,
      hasCurrentWindowData: currentStageExecutionsQuery.data !== undefined,
    }),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
                buildDateRangeFilters(
                  'startedAt',
                  dayjs(previousStartDay).startOf('day').toISOString(),
                  dayjs(previousEndDay).endOf('day').toISOString(),
                ),
              ),
            },
          ),
      ),
  });

  const { countedPipelineOptions, countedStageOptions, countedTagOptions } =
    useStagesPageFilterOptionCounts({
      effectiveEndDay,
      effectiveProjectId,
      effectiveStartDay,
      hasLatestTimestamp,
      isAuthenticated,
      isLatestMode,
      pipelineOptions,
      selectedOrganisationId: selectedOrganisationId ?? null,
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
      selectedTags,
      stageOptions,
      stageSearchValue,
      tagOptions,
    });
  const effectiveStageExecutions = useMemo(
    () =>
      currentStageExecutions.length > 0
        ? currentStageExecutions
        : buildFallbackStageExecutions(currentStages),
    [currentStageExecutions, currentStages],
  );
  const previousStageExecutions = previousStageExecutionsQuery.data?.items ?? [];
  const showPreviousExecutionComparison =
    stagePageQueryGates.shouldShowStagePreviousWindowComparison({
      showDelta,
      hasPreviousWindowData: previousStageExecutionsQuery.data !== undefined,
    });
  const stageCounts = useMemo(() => buildStageCounts(currentStages), [currentStages]);
  const stageMetricOptions = useMemo(buildBuildMetricOptions, []);
  const currentMetrics = useMemo(
    () => buildStageExecutionMetricSummary(effectiveStageExecutions, currentChartDates),
    [currentChartDates, effectiveStageExecutions],
  );
  const previousMetrics = useMemo(
    () => buildStageExecutionMetricSummary(previousStageExecutions, previousChartDates),
    [previousChartDates, previousStageExecutions],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: null,
        previousMetrics: null,
        thresholds: thresholds.stages,
        frequencyOverride: currentMetrics.frequencyPerDay,
        previousFrequencyOverride: showPreviousExecutionComparison
          ? previousMetrics.frequencyPerDay
          : null,
        avgDurationOverride: currentMetrics.avgDurationSeconds,
        previousAvgDurationOverride: showPreviousExecutionComparison
          ? previousMetrics.avgDurationSeconds
          : null,
        passRateOverride: currentMetrics.passRate,
        previousPassRateOverride: showPreviousExecutionComparison ? previousMetrics.passRate : null,
        flakeScoreOverride: currentMetrics.flakeScore,
        previousFlakeScoreOverride: showPreviousExecutionComparison
          ? previousMetrics.flakeScore
          : null,
        showDelta: showPreviousExecutionComparison,
        visibleMetricKeys: resolveVisibleMetricThresholdKeys(thresholds.stages),
      }),
    [currentMetrics, previousMetrics, showPreviousExecutionComparison, thresholds.stages],
  );
  const { metricCards } = useStageMetricCards({
    showDelta,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedProjectId: effectiveProjectId,
    stageSearchValue,
    selectedPipelineIds,
    selectedStageNames,
    selectedStatuses,
    selectedTags,
    previousStartDay,
    previousEndDay,
    baseStageFilters,
    currentStagesLength: currentStages.length,
    currentStageExecutionsLength: effectiveStageExecutions.length,
    previousStageExecutionsLength: previousStageExecutions.length,
    previousStageRowsQueryEnabled: stagePageQueryGates.shouldLoadStagePreviousWindowQuery({
      isAuthenticated,
      showDelta,
      hasPreviousWindowBounds: previousStartDay !== null && previousEndDay !== null,
      hasCurrentWindowData: currentStagesQuery.data !== undefined,
    }),
    hasPreviousStageExecutionsData: previousStageExecutionsQuery.data !== undefined,
    currentStagesLoading: currentStagesQuery.isLoading,
    currentStageCounts: stageCounts,
    stageMetricOptions,
  });
  const toolbarState = useMemo(
    () => ({
      ...buildMetricsChartToolbarState({
        search: stageSearchValue,
        setSearch: setStageSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: formatRangeWindowNoDataText(
          getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
          range,
          Boolean(selectedStartDay ?? selectedEndDay),
        ),
        dateQuickFilter: range === 'all' ? '30d' : range,
        onDateQuickFilterChange: activateComparisonForRange,
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        latestQueryStartDay: hasLatestTimestamp
          ? dayjs(latestTimestamp).format('YYYY-MM-DD')
          : null,
        latestQueryEndDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
        onFromDateChange: handleStartDayChange,
        onToDateChange: handleEndDayChange,
      }),
      selectedEntities: selectedStageNames,
      setSelectedEntities: setSelectedStageNames,
      entityOptions: countedStageOptions,
      entityLabel: 'Stage',
      selectedSuites: selectedPipelineIds,
      setSelectedSuites: setSelectedPipelineIds,
      suiteOptions: countedPipelineOptions,
      suiteLabel: 'Pipeline',
      selectedProjects,
      setSelectedProjects: handleProjectsChange,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
    }),
    [
      activateComparisonForRange,
      handleProjectsChange,
      handleEndDayChange,
      handleStartDayChange,
      countedPipelineOptions,
      countedStageOptions,
      latestTimestamp,
      hasLatestTimestamp,
      projectFilterOptions,
      range,
      selectedEndDay,
      selectedProjects,
      selectedPipelineIds,
      selectedStartDay,
      selectedStageNames,
      selectedStatuses,
      selectedTags,
      showProjectFilter,
      stageSearchValue,
    ],
  );
  const stageStatusCountsByDate = useMemo(
    () => buildStageStatusCountsByDate(effectiveStageExecutions),
    [effectiveStageExecutions],
  );
  const visibleStageStatuses = useMemo(
    () => resolveVisibleStageStatuses(selectedStatuses),
    [selectedStatuses],
  );
  const stageStatusSeries = useMemo(
    () => buildStageStatusSeries(visibleStageStatuses),
    [visibleStageStatuses],
  );
  const stageMetricBucketsByDate = useMemo(
    () => buildStageMetricBucketsByDate(effectiveStageExecutions),
    [effectiveStageExecutions],
  );
  const modelOverride = useMemo(
    () =>
      buildMetricsChartModelFromBuckets({
        statusCountsByDate: stageStatusCountsByDate,
        visibleStatuses: visibleStageStatuses,
        statusSeries: stageStatusSeries,
        metricBucketsByDate: stageMetricBucketsByDate,
        chartDates: currentChartDates,
        metricValueItems,
      }),
    [
      currentChartDates,
      metricValueItems,
      stageMetricBucketsByDate,
      stageStatusCountsByDate,
      stageStatusSeries,
      visibleStageStatuses,
    ],
  );

  const stageExecutionsByStageId = useMemo(() => {
    const map = new Map<string, PipelineStageExecutionListItemDto[]>();
    effectiveStageExecutions.forEach((execution) => {
      const stageId = `${execution.pipelineId ?? 'null'}:${execution.stageName}`;
      const items = map.get(stageId) ?? [];
      items.push(execution);
      map.set(stageId, items);
    });
    return map;
  }, [effectiveStageExecutions]);
  const historyRows = useMemo(() => {
    const builtRows = buildStageHistoryRows(
      currentStages,
      stageExecutionsByStageId,
      jobsByStageId,
      isLatestMode,
      { loadingChildStageIds: loadingStageIds },
    );
    const slicedRows = sortHistoryRows(builtRows, historySortKey, historySortDirection).slice(
      historyPage * historyPageSize,
      historyPage * historyPageSize + historyPageSize,
    );
    return slicedRows;
  }, [
    currentStages,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    isLatestMode,
    jobsByStageId,
    loadingStageIds,
    stageExecutionsByStageId,
  ]);
  const historyTotalCount = currentStages.length;
  const historyDateRangeLabel = useMemo(() => {
    if (!effectiveStartDay || !effectiveEndDay) return null;
    const startLabel = dayjs(effectiveStartDay).format('MMM D, YYYY');
    const endLabel = dayjs(effectiveEndDay).format('MMM D, YYYY');
    return `${startLabel} - ${endLabel}`;
  }, [effectiveEndDay, effectiveStartDay]);
  const historyDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        fallbackStartDay: effectiveStartDay,
        fallbackEndDay: effectiveEndDay,
        descending: true,
      }),
    [effectiveEndDay, effectiveStartDay, selectedEndDay, selectedStartDay],
  );

  const historyPageResetKey = useMemo(
    () =>
      JSON.stringify({
        effectiveEndDay,
        effectiveStartDay,
        selectedOrganisationId,
        selectedPipelineIds,
        effectiveProjectId,
        selectedStatuses,
        selectedStageNames,
        selectedTags,
        stageSearchValue,
      }),
    [
      effectiveEndDay,
      effectiveStartDay,
      selectedOrganisationId,
      selectedPipelineIds,
      effectiveProjectId,
      selectedStatuses,
      selectedStageNames,
      selectedTags,
      stageSearchValue,
    ],
  );
  useDailyHistoryPanelPageReset({ resetKey: historyPageResetKey, setPage: setHistoryPage });

  useEffect(() => {
    setExpandedRowIds(new Set());
  }, [historyPageResetKey]);

  const showPageSkeleton =
    showForcedLoadingState ||
    (currentStagesQuery.isLoading && currentStages.length === 0) ||
    (currentStageExecutionsQuery.isLoading && currentStageExecutions.length === 0);

  return {
    showPageSkeleton,
    metricSectionProps: {
      title: getChartTitle('statusTimeline'),
      searchAriaLabel: 'Search stages',
      noRowsBaseText: getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
      scope: 'stage',
      projectId: effectiveProjectId,
      availableTags: countedTagOptions,
      statusFilterLabel: getChartTitle('status'),
      metricCards,
      statusOptions: buildStatusOptions,
      toolbarState,
      modelOverride,
      loading: showForcedLoadingState,
      suppressAnimation: false,
      includeLatestQuickFilter: true,
    },
    historyRows,
    historyDayKeys,
    historyLoading:
      showForcedLoadingState ||
      isHistoryTransitionLoading ||
      currentStagesQuery.isFetching ||
      currentStageExecutionsQuery.isFetching ||
      latestBoundsQuery.isFetching,
    historyHasError:
      currentStagesQuery.isError ||
      currentStageExecutionsQuery.isError ||
      latestBoundsQuery.isError ||
      (expandedStages.length > 0 && hasStageJobsError),
    historyNoRowsText: formatRangeWindowNoDataText(
      getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
      range,
      Boolean(selectedStartDay ?? selectedEndDay),
    ),
    historyDateRangeLabel,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    historyTotalCount,
    expandedRowIds,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    setExpandedRowIds,
    tablePanelProps: {
      selectedProjectId: effectiveProjectId,
      selectedOrganisationId: selectedOrganisationId ?? null,
      tagOptions,
      dateQuickFilter: range === 'all' ? '30d' : range,
      fromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      toDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      latestQueryStartDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
      latestQueryEndDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
      projectOptions: showProjectFilter ? projectFilterOptions : [],
      stageSearchValue,
      stageSortKey,
      stageSortDirection,
      onStageSortChange: (nextSortKey, nextSortDirection) => {
        setStageSortKey(nextSortKey);
        setStageSortDirection(nextSortDirection);
      },
      stageFilterStatuses: selectedStatuses,
      stageFilterPipelineIds: selectedPipelineIds,
      stageFilterStageNames: selectedStageNames,
      stageFilterTags: selectedTags,
    },
  };
};
